import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import AIAssistantDirect from 'app/pages/AiAssistant/exposes/AIAssistant';
import { useAiAssistantSlice } from 'app/pages/AiAssistant/store';
import { AIAssistantSlideout } from 'app/components/AIAssistantSlideout';
import { layoutActions, useLayoutSlice } from 'app/pages/Layout/slice';
import {
  selectCanAccessAiAssistant,
  selectShowAiAssistant,
  selectUserProfile
} from 'app/pages/Layout/slice/selectors';
import { useRoadmapAiAssistantContext } from 'app/hooks/useRoadmapAiAssistantContext';
import { useSlideoutUrl } from 'app/hooks/useSlideoutUrl';
import { setUserEmailGetter } from 'app/pages/AiAssistant/services/authBridge';

export function AIAssistantDock(): React.ReactElement {
  useLayoutSlice();
  useAiAssistantSlice();
  const open = useSelector(selectShowAiAssistant);
  const canAccessAiAssistant = useSelector(selectCanAccessAiAssistant);
  const userProfile = useSelector(selectUserProfile);
  const dispatch = useDispatch();

  useEffect(() => {
    const email = userProfile?.email || userProfile?.mail || null;
    setUserEmailGetter(() => email);
  }, [userProfile]);
  const navigate = useNavigate();
  const location = useLocation();
  const context = useRoadmapAiAssistantContext();
  const { isSlideoutOpen } = useSlideoutUrl();

  const onClose = useCallback(() => {
    dispatch(layoutActions.closeAiAssistant());
  }, [dispatch]);

  useEffect(() => {
    if (!canAccessAiAssistant) {
      return;
    }
    const params = new URLSearchParams(location.search);
    if (params.get('openAiAssistant') !== 'true') {
      return;
    }
    params.delete('openAiAssistant');
    const qs = params.toString();
    navigate(
      { pathname: location.pathname, search: qs ? `?${qs}` : '' },
      { replace: true }
    );
    dispatch(layoutActions.openAiAssistant());
  }, [
    canAccessAiAssistant,
    dispatch,
    location.pathname,
    location.search,
    navigate
  ]);

  useEffect(() => {
    if (!canAccessAiAssistant && open) {
      dispatch(layoutActions.closeAiAssistant());
    }
  }, [canAccessAiAssistant, dispatch, open]);

  // When a moment slideout is open, AI renders inside SlideOutPanel — not this overlay.
  const showOverlay = open && !isSlideoutOpen;

  return (
    <AIAssistantSlideout isOpen={showOverlay}>
      <AIAssistantDirect
        isOpen={showOverlay}
        onClose={onClose}
        context={context}
        mode="overlay"
      />
    </AIAssistantSlideout>
  );
}
